import { faAngleLeft } from "@fortawesome/free-solid-svg-icons/faAngleLeft";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons/faAngleRight";
import { faCircle } from "@fortawesome/free-solid-svg-icons/faCircle";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import i18n from "../../i18n";
import { PageMeta } from "../../model/shared/meta/PageMeta";

import { TaskFilterReminder } from "../../model/task/types/TaskFilterReminder.enum";

import { useAppDispatch, useAppSelector } from "../../redux/store";
import { taskSliceSelectors } from "../../redux/task/task.selector";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { Spacing } from "../../theme/Spacing";
import { AtiraEmpty } from "../AtiraEmpty";
import { AtiraIcon } from "../AtiraIcon";
import { Button } from "../Button";
import { Flex } from "../Flex";
import { Info } from "../Info";
import { Spinner } from "../Spinner";
import { FontSize, Text } from "../Text";
import { FloatingTodayTasksTask } from "./FloatingTodayTasksTask";
import { TaskKind } from "../../model/task/shared/TasksKind.enum";
import { ThemeType } from "../../theme/Theme";
import { appActions } from "../../redux/app/app.slice";
import { EntityIcons } from "../../utils/GlobalIcons";
import { AppLangs } from "../../model/shared/enum/AppLangs.enum";
import { Rounded } from "../../theme/Rounded";

const TasksWrapper = styled(Flex)<{ loading?: boolean }>`
  justify-content: center;
  max-height: min(52dvh, 29rem);
  min-height: 16rem;
  overflow-y: auto;
  padding: ${Spacing.s} ${Spacing.m} ${Spacing.s} ${Spacing.s};
  pointer-events: ${({ loading }) => (loading ? "none" : "all")};
`;

const Panel = styled(Flex)`
  width: min(34rem, calc(100vw - ${Spacing.l}));
  overflow: hidden;
  border-radius: ${Rounded.lg};
  background-color: ${({ theme }) => theme.white};
`;

const Header = styled(Flex)`
  padding: ${Spacing.m} ${Spacing.m} ${Spacing.s};
  border-bottom: ${({ theme }) => `1px solid ${theme.darkSub}`};
  background-color: ${({ theme }) => theme.white};
`;

const Count = styled(Text)`
  min-width: 1.5rem;
  padding: 0 ${Spacing.s};
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.sub};
  color: ${({ theme }) => theme.darkerSub};
  text-align: center;
`;

const TabBar = styled(Flex)`
  overflow-x: auto;
  padding: ${Spacing.s} ${Spacing.m};
  border-bottom: ${({ theme }) => `1px solid ${theme.darkSub}`};
  background-color: ${({ theme }) => theme.white};
`;

const InfoWrapper = styled.div`
  padding: ${Spacing.s} ${Spacing.m} 0;
`;

const TaskTab = styled.button<{ active: boolean; kind?: TaskKind }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  flex: 0 0 auto;
  padding: ${Spacing.s} ${Spacing.m};
  border: 0;
  border-radius: ${Rounded.pill};
  color: ${({ kind, theme }) =>
    kind
      ? theme[`${kind.toLowerCase()}Task` as keyof ThemeType]
      : theme.textColor};
  background-color: ${({ active, theme }) =>
    active ? theme.lightSub : "transparent"};
  cursor: pointer;
  font: inherit;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.lightSub};
  }
`;

const EmptyWrapper = styled(Flex)`
  padding-block: ${Spacing.l};

  .ant-empty-image {
    height: 2rem;
  }

  .ant-empty-description p {
    font-size: ${FontSize.m};
  }
`;

const NavigateButton = styled(Button)`
  & > div {
    flex-direction: row-reverse !important;
  }

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

export const FloatingTodayTasksContent: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const tasks = useAppSelector(taskSliceSelectors.selectTodayTasks);
  const loading = useAppSelector(taskSliceSelectors.selectTodayTasksLoading);
  const loggedInUser = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const [activeKind, setActiveKind] = useState<TaskKind | "ALL">("ALL");

  const taskCounts = useMemo(
    () =>
      Object.values(TaskKind).reduce(
        (counts, kind) => ({
          ...counts,
          [kind]: tasks?.filter((task) => task.kind === kind).length ?? 0,
        }),
        {} as Record<TaskKind, number>,
      ),
    [tasks],
  );

  const displayedTasks = useMemo(
    () =>
      activeKind === "ALL"
        ? tasks
        : tasks?.filter((task) => task.kind === activeKind),
    [activeKind, tasks],
  );

  const onNavigateToSeeAllTasks = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();

    dispatch(appActions.setFloatingTodayTasksVisible(false));

    navigate("/tasks", {
      state: {
        useFilter: {
          reminder: TaskFilterReminder.TODAY,
        },
        meta: PageMeta.create({ page: 0, count: 50 }),
      },
    });
  };

  return (
    <Panel flexDirection="column">
      <Header alignItems="center" justifyContent="space-between" gap="m">
        <Flex flexDirection="column" gap="xs">
          <Text fontWeight="bold" fontSize="l">
            {t("floatingTasksMenu.title")}
          </Text>
        </Flex>
        <Count fontWeight="bold">{tasks?.length ?? 0}</Count>
      </Header>

      <InfoWrapper>
        <Info
          info={t("floatingTasksMenu.todayInfo", {
            name: loggedInUser?.name,
          })}
          width="100%"
        />
      </InfoWrapper>

      <TabBar gap="xs" alignItems="center">
        <TaskTab
          type="button"
          active={activeKind === "ALL"}
          onClick={() => setActiveKind("ALL")}
        >
          {t("common.all")}
          <Count fontSize="s">{tasks?.length ?? 0}</Count>
        </TaskTab>
        {Object.values(TaskKind).map((kind) => (
          <TaskTab
            type="button"
            active={activeKind === kind}
            key={kind}
            kind={kind}
            onClick={() => setActiveKind(kind)}
          >
            <AtiraIcon
              icon={faCircle}
              size="xs"
              color={`${kind.toLowerCase()}Task` as keyof ThemeType}
            />
            {t(`common.${kind.toLowerCase()}_task`)}
            <Count fontSize="s">{taskCounts[kind]}</Count>
          </TaskTab>
        ))}
      </TabBar>

      <TasksWrapper loading={loading}>
        {loading ? (
          <Flex
            style={{ zIndex: 999 }}
            opacity={1}
            alignSelf="center"
            position="absolute"
          >
            <Spinner color="main" size="3rem" />
          </Flex>
        ) : null}

        {displayedTasks?.length ? (
          <Flex
            flexDirection="column"
            flex={1}
            gap="s"
            opacity={loading ? 0.5 : 1}
          >
            {displayedTasks.map((task, index) => (
              <FloatingTodayTasksTask
                key={`${task._id}-${index}`}
                task={task}
              />
            ))}
          </Flex>
        ) : (
          <EmptyWrapper>
            <AtiraEmpty
              icon={EntityIcons.Tasks}
              description={t("floatingTasksMenu.noTasksToday")}
            />
          </EmptyWrapper>
        )}
      </TasksWrapper>

      {tasks?.length ? (
        <NavigateButton
          onClick={onNavigateToSeeAllTasks}
          icon={i18n.language === AppLangs.AR ? faAngleLeft : faAngleRight}
        >
          {t("floatingTasksMenu.allTodayTasks")}
        </NavigateButton>
      ) : null}
    </Panel>
  );
};
